<template>
  <fts-client-modal
    width="1080px"
    :title="fileName"
    append-content-class="document-modal__body"
    append-container-class="document-viewer-container"
    class="fts-document-modal"
    @closed="onClosed"
    ref="ftsClientModal"
  >
    <!-- header:icon -->
    <font-awesome-icon slot="icon" :icon="['fas', 'print']" fixed-width />
    <!-- body -->
    <template #body>
      <!-- loading indicator -->
      <div class="loading-indicator" v-if="loading">
        <fts-spinner show />
        <div class="loading-indicator-label">Getting document...</div>
      </div>
      <template v-else>
        <!-- pdf -->
        <vue-pdf-app
          theme="light"
          :config="config"
          :file-name="fileName"
          :pdf="previewDocument"
          @open="openHandler"
          @after-created="afterCreated"
        >
          <template #toolbar-right-prepend>
            <button
              type="button"
              class="toolbarButton vue-pdf-app-icon"
              :disabled="loading"
              @click="loadDocument"
            >
              <font-awesome-icon :icon="['fas', 'sync-alt']" size="lg" />
            </button>
            <!-- SS05760: the browser's own print of the PDF - text stays text. pdf.js's print
                 (hidden while this one is offered) rasterises pages and prints blurry. -->
            <button
              v-if="nativePrintSupported"
              type="button"
              class="toolbarButton print hiddenMediumView vue-pdf-app-icon print-button document-modal-native-print"
              :class="{ 'document-modal-native-print--loading': nativePrintLoading }"
              :disabled="loading || nativePrintLoading || !previewDocument"
              :title="nativePrintLoading ? 'Preparing print preview' : 'Print'"
              data-l10n-id="print"
              @click="printDocumentIframe"
            >
              <span data-l10n-id="print_label">Print</span>
            </button>
          </template>
        </vue-pdf-app>
        <!-- error -->
        <div class="document-error" v-if="error">
          <div class="document-error-label">Error</div>
          <div class="document-error-message">{{error}}</div>
        </div>
      </template>
    </template>
  </fts-client-modal>
</template>

<script>
import VuePdfApp from "vue-pdf-app";
import "vue-pdf-app/dist/icons/main.css";

import { authorizationStore } from '../store/authorizationStore';
import { classificationStore } from "../store/classificationStore";
import { clientStore } from "../store/clientStore";
import webAppConfig from "../commons/webApp.config";
import { sendWithTokenRefresh } from "../commons/documentRequest";

import FTSClientModal from "./FTSClientModal";
import FTSBtn from "./FTSBtn";
import FTSSpinner from "./FTSSpinner";

/* SS05760: WebKit (Safari, every iOS browser) does not print a PDF framed from a blob URL
   reliably, so it keeps pdf.js's own print there. */
export const supportsNativePdfPrint = (userAgent = (typeof navigator !== "undefined" && navigator.userAgent) || "") =>
  !/iPad|iPhone|iPod/i.test(userAgent) && !/^((?!chrome|chromium|crios|fxios|android|edg).)*safari/i.test(userAgent);

export default {
  data: () => {
    const nativePrintSupported = supportsNativePdfPrint();
    return {
      pdfApplication: null,
      template: null,
      nativePrintSupported,
      nativePrintFrame: null,
      nativePrintStartTimer: null,
      nativePrintLoading: false,
      nativePrintUrl: null,
      configTest: {
        sidebar: false,
        toolbar: false,
        secondaryToolbar: false
      },
      config: {
        sidebar: false,
        toolbar: {
          toolbarViewerLeft: {
            findbar: false,
            previous: false,
            next: false
          },
          toolbarViewerRight: {
            presentationMode: false,
            openFile: false,
            viewBookmark: false,
            /* pdf.js prints pages as canvas images (blurry): offered only where the native
               print is not */
            print: !nativePrintSupported
          }
        },
        secondaryToolbar: false
      },
      fileName: "preparing...",
      previewDocument: null,
      error: null,
      loading: false
    };
  },
  components: {
    VuePdfApp,
    "fts-client-modal": FTSClientModal,
    "fts-btn": FTSBtn,
    "fts-spinner": FTSSpinner
  },
  methods: {
    clearNativePrintTimer(key) {
      if (!this[key]) {
        return;
      }

      window.clearTimeout(this[key]);
      this[key] = null;
    },
    fallbackPdfJsPrint() {
      try {
        if (this.pdfApplication && this.pdfApplication.eventBus) {
          this.pdfApplication.eventBus.dispatch("print");
          return;
        }
      } catch (err) {
        console.warn("pdf.js print fallback failed", err);
      }

      window.print();
    },
    async show(template) {
      this.template = template;
      this.$refs.ftsClientModal.show();
      await this.loadDocument();
    },
    /* SS06258: the document was closed on the other preview device */
    close() {
      this.$refs.ftsClientModal.close();
    },
    /* SS06258: the device preview reports a closed document to the other device */
    onClosed() {
      this.reset();
      this.$emit("closed");
    },
    reset() {
      this.cleanupNativePrint();
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    /* The print frame and its blob URL live until the modal closes, reloads, prints again or is
       destroyed - never less (SS05760). */
    cleanupNativePrint() {
      this.nativePrintLoading = false;

      this.clearNativePrintTimer("nativePrintStartTimer");

      if (this.nativePrintFrame) {
        this.nativePrintFrame.onload = null;
        this.nativePrintFrame.onerror = null;
        if (this.nativePrintFrame.parentNode) {
          this.nativePrintFrame.parentNode.removeChild(this.nativePrintFrame);
        }
        this.nativePrintFrame = null;
      }

      if (this.nativePrintUrl) {
        URL.revokeObjectURL(this.nativePrintUrl);
        this.nativePrintUrl = null;
      }
    },
    printDocumentIframe() {
      if (!this.previewDocument || this.loading || this.nativePrintLoading) {
        return;
      }

      this.cleanupNativePrint();
      this.nativePrintLoading = true;

      try {
        const blob = new Blob([this.previewDocument], { type: "application/pdf" });
        const iframe = document.createElement("iframe");
        const blobUrl = URL.createObjectURL(blob);

        /* SS05760: off screen but full size. Chrome's PDF viewer paginates a frame with no real
           layout box (the old 1px x 1px, opacity 0) to nothing and prints a blank page. */
        iframe.style.position = "fixed";
        iframe.style.left = "-10000px";
        iframe.style.top = "0";
        iframe.style.width = "816px";
        iframe.style.height = "1056px";
        iframe.style.border = "0";
        iframe.style.pointerEvents = "none";
        iframe.setAttribute("aria-hidden", "true");
        iframe.setAttribute("tabindex", "-1");

        this.nativePrintFrame = iframe;
        this.nativePrintUrl = blobUrl;

        const cleanup = (shouldFallback = false) => {
          this.cleanupNativePrint();
          if (shouldFallback) {
            this.fallbackPdfJsPrint();
          }
        };

        iframe.onload = () => {
          try {
            const frameWindow = iframe.contentWindow;
            if (!frameWindow) {
              throw new Error("print iframe window unavailable");
            }

            /* SS05760: nothing is torn down on afterprint or when this window regains focus -
               Chrome's print preview is a separate surface that can still be reading the frame
               then, and revoking the blob under it printed a blank page. cleanupNativePrint runs
               when the modal closes, reloads or prints again. */
            this.nativePrintStartTimer = window.setTimeout(() => {
              if (this.nativePrintFrame !== iframe) {
                return;
              }
              this.nativePrintLoading = false;
              try {
                frameWindow.focus();
                frameWindow.print();
              } catch (err) {
                console.warn("native iframe print failed", err);
                cleanup(true);
              }
            }, 150);
          } catch (err) {
            console.warn("native iframe print failed", err);
            cleanup(true);
          }
        };

        iframe.onerror = () => {
          cleanup(true);
        };

        document.body.appendChild(iframe);
        iframe.src = blobUrl;
      } catch (err) {
        console.warn("native iframe print setup failed", err);
        this.cleanupNativePrint();
        this.fallbackPdfJsPrint();
      }
    },
    async loadDocument() {
      this.cleanupNativePrint();
      this.previewDocument = null;
      this.error = null;
      this.loading = true;
      try {
        await this.fetchDocument();
      } catch (err) {
        /* last resort, so the modal never stays stuck on "Getting document..." */
        console.error("error", err);
        this.error = 'Something went wrong. Please try again.';
        this.fileName = 'Template';
      } finally {
        this.loading = false;
      }
    },
    async fetchDocument() {
      let template = this.template;
      /* base 64 to array buffer */
      function _base64ToArrayBuffer(base64) {
        var binary_string = window.atob(base64);
        var len = binary_string.length;
        var bytes = new Uint8Array(len);
        for (var i = 0; i < len; i++) {
          bytes[i] = binary_string.charCodeAt(i);
        }
        return bytes.buffer;
      }
      /* request */
      const request = (method, url, body) => {
        let xhr = new XMLHttpRequest();
        return new Promise(resolve => {
          xhr.open(method, url, true);
          const token = JSON.parse(localStorage.getItem("ls.authorizationData"))["access_token"];
          xhr.setRequestHeader("Content-type", "application/json;charset=UTF-8");
          xhr.setRequestHeader("authorization", "bearer " + token);
          xhr.setRequestHeader("wsId", authorizationStore.getters.runAsWsId || clientStore.getters.workspaceId);
          xhr.setRequestHeader("tz", Intl.DateTimeFormat().resolvedOptions().timeZone)
          xhr.responseType = "arraybuffer";
          xhr.onload = () => resolve(xhr);
          xhr.onerror = () => resolve({
            status: xhr.status,
            statusText: xhr.statusText,
            response: xhr.response,
            responseText: xhr.responseText
          });
          xhr.onabort = () => resolve({
            status: 0,
            statusText: "Request canceled."
          });
          xhr.ontimeout = () => resolve({
            status: 0,
            statusText: "Request timed out. Please try again."
          });
          xhr.send(body);
        });
      }
      /* error handling */
      const errorHandling = (err = {}) => {
        if (err.response && err.response.data && err.response.data.message) {
          this.error = err.response.data.message;
        } else if (err.statusText) {
          this.error = err.statusText;
        } else {
          this.error = 'Something went wrong. Please try again.'
        }
        this.fileName = 'Template';
      }

      /* SS06238: the shells pass { method, url, body } from buildDocumentRequest - a POST when the
         document model is too long for a GET. A bare `url` is still a GET. */
      const documentRequest = template.request || (template.url ? { method: "GET", url: template.url } : null);
      if (
        documentRequest
        && template.parameters
        && !template.parameters.template
      ) {
        try {
          /* SS05651: an expired access token is renewed and the document asked for again, as the
             shared HTTP client does for every other request (`request` reads the token each time). */
          let req = await sendWithTokenRefresh(
            () => request(
              documentRequest.method,
              webAppConfig.baseURL ? webAppConfig.baseURL + documentRequest.url : documentRequest.url,
              documentRequest.body
            ),
            () => authorizationStore.dispatch("refreshToken")
          );
          if (req && req.status === 200) {
            let filename = "Template";
            let disposition = req.getResponseHeader("Content-Disposition");
            if (disposition) {
              const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
              const matches = filenameRegex.exec(disposition);
              if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, "");
              }
              this.fileName = decodeURIComponent(filename).replaceAll(/\n/g, "");
            } else {
              this.fileName = "Template";
            }

            let type = req.getResponseHeader("Content-Type");
            let blob;
            if (typeof File === "function") {
              try {
                blob = new File([req.response], filename, { type: type });
              } catch (err) {
                console.error(err);
              }
            }
            if (typeof blob === "undefined") {
              blob = new Blob([req.response], { type: type });
            }
            if (typeof window.navigator.msSaveBlob !== "undefined") {
              // IE workaround for "HTML7007: One or more blob URLs were revoked by closing the blob for which they were created. These URLs will no longer resolve as the data backing the URL has been freed."
              window.navigator.msSaveBlob(blob, filename);
            } else {
              await blob.arrayBuffer().then(buffer => {
                this.previewDocument = buffer;
              });
            }
          } else {
            errorHandling({
              status: req.status,
              statusText: req.statusText
            });
          }
        } catch(err) {
          console.error("error", err);
          errorHandling(err);
        }
      } else {
        try {
          const res = await classificationStore.dispatch("loadDocument", template.parameters);
          this.previewDocument = _base64ToArrayBuffer(res.data.bytes);
          this.fileName = decodeURIComponent(res.data.filename).replaceAll(/\n/g, "");
        } catch (err) {
          console.error("error", err);
          errorHandling(err);
        }
      }
    },
    openHandler(pdfApplication) {
      try {
        pdfApplication.preferences.prefs.printResolution = 300;
        pdfApplication._readPreferences();
      } catch (err) { console.warn("unable set print resolution", err); }
      try {
        pdfApplication.pdfViewer.container.classList.add("fts-scrollbar");
      } catch (err) { console.warn("unable append scrollbar", err); }
      this.pdfApplication = pdfApplication;
    },
    afterCreated(pdfApplication) {
      /* emitted only once when Pdfjs library is binded to vue component. Can be used to set Pdfjs config before pdf document opening. */
    }
  },
  beforeDestroy() {
    this.cleanupNativePrint();
  },
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

.fts-document-modal {
  .document-viewer-container {
    height: 100%;
    .document-modal__body {
      position: relative;
      padding: 0;
    }
  }
  .pdf-app .pdfViewer .page {
    box-sizing: content-box;
    border-image: none;
  }
  .loading-indicator {
    @include absolute-center-middle;
    z-index: 999;
    .loading-indicator-label {
      margin-top: 1rem;
    }
  }
  .document-error {
    @include absolute-center-middle;
    text-align: center;
    padding: 0.75rem 1rem;
    border-radius: 0.25rem;
    box-shadow: var(--fts-elevation-1);
    background-color: var(--fts-card-background);
    .document-error-label {
      margin-bottom: 0.125rem;
      font-weight: bold;
      font-size: 1rem;
      line-height: 1.5rem;
      color: var(--fts-danger);
    }
    .document-error-message {
      font-size: 0.875rem;
      line-height: 1.25rem;
      color: var(--fts-text);
    }
  }
  .document-modal-native-print {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    gap: 0.125rem;
    padding-right: 0.25rem;
    width: auto;
    .fts-release-badge {
      transform: scale(0.85);
      transform-origin: center;
    }
  }
  .pdf-app #toolbarContainer button.document-modal-native-print.document-modal-native-print--loading,
  .pdf-app #toolbarContainer button.document-modal-native-print.document-modal-native-print--loading:hover {
    opacity: 0.5;
    color: var(--pdf-toolbar-font-color);
    cursor: progress;
  }
}
</style>
